import { supabase } from '../lib/supabase';
import { addWeeks, setHours, setMinutes, setSeconds, setMilliseconds, addHours, startOfDay, addDays } from 'date-fns';


export const calendarService = {
  // Generate Monday slots for the next 4 weeks
  async generateMondaySlots() {
    // Try to get authenticated user, but allow generation without auth
    const { data: { user } } = (await supabase?.auth?.getUser()) || { data: { user: null } };
    
    // Use authenticated user ID or null (system-generated slots)
    const adminId = user?.id || null;

    const slots = [];
    const now = new Date();
    const today = startOfDay(now);
    
    // Find the next Monday from today
    let firstMonday;
    const currentDayOfWeek = today?.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    if (currentDayOfWeek === 1) {
      // Today is Monday - use today as first Monday
      firstMonday = today;
    } else if (currentDayOfWeek === 0) {
      // Today is Sunday - next Monday is tomorrow
      firstMonday = addDays(today, 1);
    } else {
      // Calculate days until next Monday
      const daysUntilMonday = 8 - currentDayOfWeek; // Days from current day to next Monday
      firstMonday = addDays(today, daysUntilMonday);
    }

    // Time slots: 10:00 AM, 11:30 AM, 2:00 PM, 3:30 PM
    const timeSlots = [
      { hours: 10, minutes: 0 },  // 10:00 AM
      { hours: 11, minutes: 30 }, // 11:30 AM
      { hours: 14, minutes: 0 },  // 2:00 PM
      { hours: 15, minutes: 30 }  // 3:30 PM
    ];

    // Generate slots for 4 consecutive Mondays
    for (let week = 0; week < 4; week++) {
      const monday = addWeeks(firstMonday, week);
      
      for (const timeSlot of timeSlots) {
        let startTime = setHours(monday, timeSlot?.hours);
        startTime = setMinutes(startTime, timeSlot?.minutes);
        startTime = setSeconds(startTime, 0);
        startTime = setMilliseconds(startTime, 0);
        
        const endTime = addHours(startTime, 1); // 1-hour slots
        
        slots?.push({
          admin_id: adminId,
          start_time: startTime?.toISOString(),
          end_time: endTime?.toISOString(),
          is_available: true
        });
      }
    }

    // Insert all slots
    const { data, error } = await supabase?.from('availability_slots')?.insert(slots)?.select();

    if (error) throw error;
    
    return data?.map(slot => ({
      id: slot?.id,
      adminId: slot?.admin_id,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      isAvailable: slot?.is_available,
      createdAt: slot?.created_at,
      updatedAt: slot?.updated_at
    })) || [];
  },

  // Get booked time slots from consultation_bookings
  async getBookedSlots(startDate, endDate) {
    const { data, error } = await supabase
      ?.from('consultation_bookings')
      ?.select('scheduled_start, scheduled_end, status')
      ?.in('status', ['pending', 'confirmed']) // Only consider active bookings
      ?.gte('scheduled_start', startDate)
      ?.lte('scheduled_start', endDate);

    if (error) throw error;
    
    return data?.map(booking => ({
      scheduledStart: booking?.scheduled_start,
      scheduledEnd: booking?.scheduled_end,
      status: booking?.status
    })) || [];
  },

  // Availability Slots Management
  async getAvailableSlots(startDate, endDate) {
    // Get all Monday slots from availability_slots table
    const { data: slots, error: slotsError } = await supabase
      ?.from('availability_slots')
      ?.select('*')
      ?.eq('is_available', true)
      ?.gte('start_time', startDate)
      ?.lte('start_time', endDate)
      ?.order('start_time', { ascending: true });

    if (slotsError) throw slotsError;
    
    // Get all existing bookings in the same date range
    const bookedSlots = await this.getBookedSlots(startDate, endDate);
    
    // Create a Set of booked start times for quick lookup
    const bookedStartTimes = new Set(
      bookedSlots?.map(booking => new Date(booking?.scheduledStart)?.toISOString())
    );
    
    // Filter out slots that have existing bookings
    const availableSlots = slots?.filter(slot => {
      const slotStartTime = new Date(slot?.start_time)?.toISOString();
      return !bookedStartTimes?.has(slotStartTime);
    }) || [];
    
    return availableSlots?.map(slot => ({
      id: slot?.id,
      adminId: slot?.admin_id,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      isAvailable: slot?.is_available,
      createdAt: slot?.created_at,
      updatedAt: slot?.updated_at
    }));
  },

  async createAvailabilitySlot(slotData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('availability_slots')?.insert({
        admin_id: user?.id,
        start_time: slotData?.startTime,
        end_time: slotData?.endTime,
        is_available: true
      })?.select()?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      adminId: data?.admin_id,
      startTime: data?.start_time,
      endTime: data?.end_time,
      isAvailable: data?.is_available,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateAvailabilitySlot(slotId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.startTime !== undefined) dbUpdates.start_time = updates?.startTime;
    if (updates?.endTime !== undefined) dbUpdates.end_time = updates?.endTime;
    if (updates?.isAvailable !== undefined) dbUpdates.is_available = updates?.isAvailable;

    const { data, error } = await supabase?.from('availability_slots')?.update(dbUpdates)?.eq('id', slotId)?.eq('admin_id', user?.id)?.select()?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      adminId: data?.admin_id,
      startTime: data?.start_time,
      endTime: data?.end_time,
      isAvailable: data?.is_available,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteAvailabilitySlot(slotId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('availability_slots')?.delete()?.eq('id', slotId)?.eq('admin_id', user?.id);

    if (error) throw error;
  },

  async getAdminAvailabilitySlots(startDate, endDate) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('availability_slots')?.select('*')?.eq('admin_id', user?.id)?.gte('start_time', startDate)?.lte('start_time', endDate)?.order('start_time', { ascending: true });

    if (error) throw error;
    
    return data?.map(slot => ({
      id: slot?.id,
      adminId: slot?.admin_id,
      startTime: slot?.start_time,
      endTime: slot?.end_time,
      isAvailable: slot?.is_available,
      createdAt: slot?.created_at,
      updatedAt: slot?.updated_at
    })) || [];
  },

  // Consultation Bookings
  async createBooking(bookingData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    /* 
     * CONSULTATION BOOKING INSERT FUNCTION
     * 
     * This function inserts a new booking record into the consultation_bookings table.
     * After successful insert, it directly calls the send-booking-confirmation edge function
     * to send a confirmation email to the client.
     * 
     * SQL EQUIVALENT:
     * INSERT INTO public.consultation_bookings (...) VALUES (...) RETURNING *;
     * 
     * EMAIL NOTIFICATION:
     * After the INSERT completes, this function directly invokes the 
     * 'send-booking-confirmation' edge function via HTTP POST.
     * 
     * Edge Function: send-booking-confirmation
     * Email Provider: Resend
     */
    
    const { data, error } = await supabase?.from('consultation_bookings')?.insert({
        title: bookingData?.title,
        description: bookingData?.description || null,
        client_name: bookingData?.clientName,
        client_email: bookingData?.clientEmail,
        client_phone: bookingData?.clientPhone || null,
        company: bookingData?.company || null,
        service_interest: bookingData?.serviceInterest || null,
        scheduled_start: bookingData?.scheduledStart,
        scheduled_end: bookingData?.scheduledEnd,
        duration_minutes: bookingData?.durationMinutes || 60,
        status: 'pending'
      })?.select()?.single();

    if (error) throw error;
    
    // Send confirmation email via Supabase edge function
    try {
      // Format date and time for email
      const startDate = new Date(bookingData?.scheduledStart);
      const endDate = bookingData?.scheduledEnd ? new Date(bookingData?.scheduledEnd) : null;
      const formattedDate = startDate?.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const formattedTime = startDate?.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      const endTime = endDate ? endDate?.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) : null;

      // Build email HTML
      const emailHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Consultation Confirmed</title>
          </head>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="color: white; margin: 0; font-size: 28px;">Consultation Confirmed! 🎉</h1>
            </div>
            
            <div style="background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px;">
              <p style="font-size: 16px; margin-bottom: 20px;">Hi ${bookingData?.clientName},</p>
              
              <p style="font-size: 16px; margin-bottom: 20px;">
                Thank you for scheduling a consultation with us! We're excited to discuss how we can help ${bookingData?.company ? `<strong>${bookingData?.company}</strong>` : 'your organization'} achieve its goals.
              </p>
              
              <div style="background: white; padding: 25px; border-radius: 8px; border-left: 4px solid #667eea; margin: 25px 0;">
                <h2 style="color: #667eea; margin-top: 0; font-size: 20px;">Appointment Details</h2>
                
                <table style="width: 100%; border-collapse: collapse;">
                  <tr>
                    <td style="padding: 10px 0; font-weight: bold; color: #555;">📅 Date:</td>
                    <td style="padding: 10px 0;">${formattedDate}</td>
                  </tr>
                  <tr>
                    <td style="padding: 10px 0; font-weight: bold; color: #555;">🕐 Time:</td>
                    <td style="padding: 10px 0;">${formattedTime}${endTime ? ` - ${endTime}` : ''}</td>
                  </tr>
                  ${bookingData?.title ? `
                  <tr>
                    <td style="padding: 10px 0; font-weight: bold; color: #555;">📋 Topic:</td>
                    <td style="padding: 10px 0;">${bookingData?.title}</td>
                  </tr>
                  ` : ''}
                  ${bookingData?.serviceInterest ? `
                  <tr>
                    <td style="padding: 10px 0; font-weight: bold; color: #555;">💼 Service:</td>
                    <td style="padding: 10px 0;">${bookingData?.serviceInterest}</td>
                  </tr>
                  ` : ''}
                </table>
              </div>
              
              <div style="background: #fff3cd; padding: 20px; border-radius: 8px; border-left: 4px solid #ffc107; margin: 25px 0;">
                <h3 style="color: #856404; margin-top: 0; font-size: 18px;">📝 What to Prepare</h3>
                <ul style="color: #856404; margin: 10px 0; padding-left: 20px;">
                  <li>Brief overview of your current data infrastructure</li>
                  <li>Key challenges you're facing</li>
                  <li>Your goals for this consultation</li>
                  <li>Any specific questions you'd like to discuss</li>
                </ul>
              </div>
              
              <p style="font-size: 16px; margin-top: 25px;">
                If you need to reschedule or have any questions before our meeting, please don't hesitate to reach out.
              </p>
              
              <p style="font-size: 16px; margin-top: 20px;">
                We look forward to speaking with you!
              </p>
              
              <p style="font-size: 16px; margin-top: 20px;">
                Best regards,<br>
                <strong>The Northstar Team</strong>
              </p>
            </div>
            
            <div style="text-align: center; padding: 20px; color: #666; font-size: 12px;">
              <p>This is an automated confirmation email. Please do not reply directly to this message.</p>
            </div>
          </body>
        </html>
      `;

      const { data: emailData, error: emailError } = await supabase?.functions?.invoke('send-email', {
        body: {
          to: bookingData?.clientEmail,
          subject: `Consultation Confirmed - ${formattedDate} at ${formattedTime}`,
          html: emailHtml
        }
      });
      
      if (emailError) {
        console.error('Error sending confirmation email:', emailError);
        console.error('Email error details:', {
          message: emailError?.message,
          status: emailError?.status,
          statusText: emailError?.statusText,
          context: emailError?.context
        });
        // Log the response data if available
        if (emailData) {
          console.error('Email response data:', emailData);
        }
        // Don't throw - booking succeeded, email is secondary
      } else if (emailData) {
        console.log('Email sent successfully:', emailData);
      }
    } catch (emailError) {
      console.error('Error sending confirmation email:', emailError);
      console.error('Caught email error details:', {
        message: emailError?.message,
        name: emailError?.name,
        stack: emailError?.stack
      });
      // Don't throw - booking succeeded, email is secondary
    }
    
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      clientName: data?.client_name,
      clientEmail: data?.client_email,
      clientPhone: data?.client_phone,
      company: data?.company,
      serviceInterest: data?.service_interest,
      scheduledStart: data?.scheduled_start,
      scheduledEnd: data?.scheduled_end,
      durationMinutes: data?.duration_minutes,
      status: data?.status,
      meetingNotes: data?.meeting_notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getUserBookings(userId) {
    const { data, error } = await supabase?.from('consultation_bookings')?.select('*')?.eq('client_id', userId)?.order('scheduled_start', { ascending: true });

    if (error) throw error;
    
    return data?.map(booking => ({
      id: booking?.id,
      title: booking?.title,
      description: booking?.description,
      clientName: booking?.client_name,
      clientEmail: booking?.client_email,
      clientPhone: booking?.client_phone,
      company: booking?.company,
      serviceInterest: booking?.service_interest,
      scheduledStart: booking?.scheduled_start,
      scheduledEnd: booking?.scheduled_end,
      durationMinutes: booking?.duration_minutes,
      status: booking?.status,
      meetingNotes: booking?.meeting_notes,
      createdAt: booking?.created_at,
      updatedAt: booking?.updated_at
    })) || [];
  },

  async getAllBookings() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('consultation_bookings')?.select('*')?.order('scheduled_start', { ascending: true });

    if (error) throw error;
    
    return data?.map(booking => ({
      id: booking?.id,
      title: booking?.title,
      description: booking?.description,
      clientName: booking?.client_name,
      clientEmail: booking?.client_email,
      clientPhone: booking?.client_phone,
      company: booking?.company,
      serviceInterest: booking?.service_interest,
      scheduledStart: booking?.scheduled_start,
      scheduledEnd: booking?.scheduled_end,
      durationMinutes: booking?.duration_minutes,
      status: booking?.status,
      meetingNotes: booking?.meeting_notes,
      createdAt: booking?.created_at,
      updatedAt: booking?.updated_at
    })) || [];
  },

  async updateBooking(bookingId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.status !== undefined) dbUpdates.status = updates?.status;
    if (updates?.meetingNotes !== undefined) dbUpdates.meeting_notes = updates?.meetingNotes;
    if (updates?.scheduledStart !== undefined) dbUpdates.scheduled_start = updates?.scheduledStart;
    if (updates?.scheduledEnd !== undefined) dbUpdates.scheduled_end = updates?.scheduledEnd;

    const { data, error } = await supabase?.from('consultation_bookings')?.update(dbUpdates)?.eq('id', bookingId)?.select()?.single();

    if (error) throw error;
    
    return {
      id: data?.id,
      title: data?.title,
      description: data?.description,
      clientName: data?.client_name,
      clientEmail: data?.client_email,
      clientPhone: data?.client_phone,
      company: data?.company,
      serviceInterest: data?.service_interest,
      scheduledStart: data?.scheduled_start,
      scheduledEnd: data?.scheduled_end,
      durationMinutes: data?.duration_minutes,
      status: data?.status,
      meetingNotes: data?.meeting_notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async cancelBooking(bookingId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('consultation_bookings')?.update({ status: 'cancelled' })?.eq('id', bookingId)?.eq('client_id', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  }
};

export default calendarService;

/**
 * TEST FUNCTION: Diagnostic booking insert with detailed error logging
 * This function calls a PostgreSQL function that logs each step of the insert process
 * Use this to identify exactly where the booking submission is breaking
 * 
 * @param {Object} bookingData - The booking form data
 * @returns {Promise<Object>} Detailed diagnostic results
 */
export const testBookingInsert = async (bookingData) => {
  try {
    console.log('🔍 TEST INSERT: Starting diagnostic test...', bookingData);
    
    // Call the diagnostic PostgreSQL function
    const { data, error } = await supabase?.rpc('test_booking_insert', {
      p_client_name: bookingData?.clientName,
      p_client_email: bookingData?.clientEmail,
      p_client_phone: bookingData?.clientPhone || null,
      p_preferred_date: bookingData?.preferredDate,
      p_preferred_time: bookingData?.preferredTime,
      p_consultation_type: bookingData?.consultationType,
      p_business_context: bookingData?.businessContext || null
    });

    if (error) {
      console.error('🔍 TEST INSERT: RPC call failed', error);
      return {
        success: false,
        step: 'RPC call',
        error: error?.message,
        details: error
      };
    }

    console.log('🔍 TEST INSERT: Result', data);
    return data;

  } catch (err) {
    console.error('🔍 TEST INSERT: Unexpected error', err);
    return {
      success: false,
      step: 'JavaScript catch block',
      error: err?.message,
      details: err
    };
  }
};